/** Shown when an author supplies a link URL but no wording for it. */
export const defaultCtaLabel = "Learn more";

/** Shown when an author supplies a video URL but no wording for it. */
export const defaultVideoLabel = "Watch video";

/** Client mirror of the server's http/https check. Lenient on purpose: the server decides. */
export function isAbsoluteHttpUrl(value: string): boolean {
    // The URL parser fills in missing slashes (`http:foo.com`), so check the prefix first.
    if (!/^https?:\/\/[^/]/i.test(value)) {
        return false;
    }

    try {
        const url = new URL(value);
        // .NET rejects an empty label (`foo..com`) but accepts a trailing dot.
        const hasEmptyLabel = url.hostname.replace(/\.$/, "").split(".").includes("");
        return (url.protocol === "http:" || url.protocol === "https:") && !hasEmptyLabel;
    } catch {
        return false;
    }
}

/** Authors schedule in Central Time, wherever they are. */
export const centralTimeZone = "America/Chicago";

const centralWallClockFormat = new Intl.DateTimeFormat("en-US", {
    timeZone: centralTimeZone,
    // h23, not hour12: false, which can print midnight as 24.
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
});

export type CentralTimeProblem = "springForwardGap" | "fallBackOverlap";

export type CentralToUtcResult = { utc: Date } | { problem: CentralTimeProblem };

/** CT is UTC-5 or UTC-6, so try both: no match is the spring-forward gap, two is the fall-back overlap. */
export function centralToUtc(
    year: number,
    month: number,
    day: number,
    hour: number,
    minute: number,
): CentralToUtcResult {
    const wanted = [year, month, day, hour, minute].join();
    const matches = [5, 6]
        .map((offsetHours) => Date.UTC(year, month - 1, day, hour + offsetHours, minute))
        .filter((candidate) => centralWallClock(candidate) === wanted);

    if (matches.length === 0) {
        return { problem: "springForwardGap" };
    }
    if (matches.length > 1) {
        return { problem: "fallBackOverlap" };
    }
    return { utc: new Date(matches[0]) };
}

export interface ICentralParts {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
}

/** The CT date and time of a moment. */
export function centralParts(utcMilliseconds: number): ICentralParts {
    const parts = Object.fromEntries(
        centralWallClockFormat
            .formatToParts(utcMilliseconds)
            .map((part) => [part.type, part.value]),
    );
    return {
        year: Number(parts.year),
        month: Number(parts.month),
        day: Number(parts.day),
        hour: Number(parts.hour),
        minute: Number(parts.minute),
    };
}

function centralWallClock(utcMilliseconds: number): string {
    const { year, month, day, hour, minute } = centralParts(utcMilliseconds);
    return [year, month, day, hour, minute].join();
}

// Some runtimes put a narrow no-break space before AM/PM.
const narrowNoBreakSpace = /\u202F/g;

const centralDateTimeFormat = new Intl.DateTimeFormat("en-US", {
    timeZone: centralTimeZone,
    dateStyle: "medium",
    timeStyle: "short",
});
const centralDateFormat = new Intl.DateTimeFormat("en-US", {
    timeZone: centralTimeZone,
    dateStyle: "medium",
});
const centralTimeFormat = new Intl.DateTimeFormat("en-US", {
    timeZone: centralTimeZone,
    timeStyle: "short",
});

/** "Oct 20, 2026, 9:00 AM CT" */
export function formatCentralDateTime(value: Date): string {
    return `${centralDateTimeFormat.format(value).replaceAll(narrowNoBreakSpace, " ")} CT`;
}

/** "Oct 20, 2026 at 9:00 AM CT" */
export function formatCentralDateAtTime(value: Date): string {
    const date = centralDateFormat.format(value);
    const time = centralTimeFormat.format(value).replaceAll(narrowNoBreakSpace, " ");
    return `${date} at ${time} CT`;
}

const appHost = "buildertrend.net";

/** True only for the app itself. Subdomains (help centre, learn, other environments) are separate sites. */
export function isInternalUrl(url: string): boolean {
    try {
        return new URL(url).hostname.toLowerCase() === appHost;
    } catch {
        return false;
    }
}
